import type * as ReportTypeViewTableAPI from 'ab-api/web/workOrderReport/reportTypeViewTable';

import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';
import { Type as ReportBlockFieldType } from '@acceligentllc/shared/enums/reportBlockField';
export enum ReportTypeViewTableAdditionalFilterType {
	JOB = 'JOB',
	FIELD = 'FIELD',
}
export enum ComparatorType {
	EQUAL = 'EQUAL',
	NOT_EQUAL = 'NOT_EQUAL',
	GREATER = 'GREATER',
	LESS = 'LESS',
	GREATER_OR_EQUAL = 'GREATER_OR_EQUAL',
	LESS_OR_EQUAL = 'LESS_OR_EQUAL',
	RANGE = 'RANGE',
	EMPTY = 'EMPTY'
}

export const ComparatorLabelMap: Record<ComparatorType, string> = {
	EQUAL: 'Equal',
	GREATER: 'Greater Than',
	GREATER_OR_EQUAL: 'Greater Than or Equal To',
	LESS: 'Less Than',
	LESS_OR_EQUAL: 'Less Than or Equal To',
	NOT_EQUAL: 'Not Equal To',
	RANGE: 'Range',
	EMPTY: 'Empty',
};

type NonNumberComparatorType = ComparatorType.EQUAL;

export const BOOLEAN_FILTER_TYPES = [
	ReportBlockFieldType.BOOLEAN,
	ReportBlockFieldType.COMPLETION,
] as const;

export function isBooleanType(
	type: ReportBlockFieldType
): type is typeof BOOLEAN_FILTER_TYPES[number] {
	return BOOLEAN_FILTER_TYPES.includes(type as typeof BOOLEAN_FILTER_TYPES[number]);
}

export type ReportTypeViewFieldBooleanFilter = {
	type: typeof BOOLEAN_FILTER_TYPES[number];
	comparator: NonNumberComparatorType;
	value: boolean;
	name: string;
	reportBlockFieldOptionId: string;
	unit?: never;
};

export const TEXTUAL_FILTER_TYPES = [
	ReportBlockFieldType.ADDRESS,
	ReportBlockFieldType.DROPDOWN,
	ReportBlockFieldType.ID,
	ReportBlockFieldType.IMAGE,
	ReportBlockFieldType.TEXTUAL_ATTRIBUTE,
	ReportBlockFieldType.TIME,
	ReportBlockFieldType.INFORMATION,
] as const;

export function isTextualType(
	type: ReportBlockFieldType
): type is typeof TEXTUAL_FILTER_TYPES[number] {
	return TEXTUAL_FILTER_TYPES.includes(type as typeof TEXTUAL_FILTER_TYPES[number]);
}

export type ReportTypeViewFieldTextualFilter = {
	type: typeof TEXTUAL_FILTER_TYPES[number];
	comparator: NonNumberComparatorType;
	value: string[];
	name: string;
	reportBlockFieldOptionId: string;
	unit?: never;
};

export const NUMERIC_FILTER_TYPES = [
	ReportBlockFieldType.CALCULATED,
	ReportBlockFieldType.NUMERIC_ATTRIBUTE,
] as const;

export function isNumericType(
	type: ReportBlockFieldType
): type is typeof NUMERIC_FILTER_TYPES[number] {
	return NUMERIC_FILTER_TYPES.includes(type as typeof NUMERIC_FILTER_TYPES[number]);
}
type ReportTypeViewTableNumericRangeType = {
	type: typeof NUMERIC_FILTER_TYPES[number];
	comparator: ComparatorType.RANGE;
	value: {
		min: Nullable<number>;
		max: Nullable<number>;
	};
	name: string;
	reportBlockFieldOptionId: string;
	unit: Nullable<QuantityUnitType>;
};

type ReportTypeViewTableNumericNonRangeType = {
	type: typeof NUMERIC_FILTER_TYPES[number];
	comparator: ComparatorType.LESS_OR_EQUAL
	| ComparatorType.EQUAL
	| ComparatorType.GREATER
	| ComparatorType.GREATER_OR_EQUAL
	| ComparatorType.NOT_EQUAL
	| ComparatorType.LESS
	| ComparatorType.EMPTY;
	value: Nullable<number>;
	name: string;
	reportBlockFieldOptionId: string;
	unit: Nullable<QuantityUnitType>;
};

export type ReportTypeViewFieldNumericFilter = ReportTypeViewTableNumericRangeType | ReportTypeViewTableNumericNonRangeType;

export type ReportTypeViewFieldSignatureFilter = {
	type: ReportBlockFieldType.SIGNATURE;
	comparator: NonNumberComparatorType;
	/**
	 * Signee id
	 */
	value: Nullable<{
		id: number;
		fullName: string;
	}>[];
	name: string;
	reportBlockFieldOptionId: string;
	unit?: never;
};

export type ReportTypeViewFieldEquipmentFilter = {
	type: ReportBlockFieldType.EQUIPMENT;
	comparator: NonNumberComparatorType;
	value: {
		/**
		 * Equipment Id
		 */
		id: number;
		code: string;
		specification: Nullable<string>;
	}[];
	name: string;
	reportBlockFieldOptionId: string;
	unit?: never;
};

export const OTHER_FILTER_TYPES = [
	ReportBlockFieldType.IMMUTABLE_IMAGE,
	ReportBlockFieldType.LINE_BREAK,
	ReportBlockFieldType.IMMUTABLE_TEXT,
] as const;

export function isOtherFilterType(
	type: ReportBlockFieldType
): type is typeof OTHER_FILTER_TYPES[number] {
	return OTHER_FILTER_TYPES.includes(type as typeof OTHER_FILTER_TYPES[number]);
}
export type ReportTypeViewFieldOtherFilter = {
	type: typeof OTHER_FILTER_TYPES[number];
	comparator?: never;
	value?: never;
	name?: never;
	reportBlockFieldOptionId?: never;
	unit?: never;
};
export type ReportTypeViewFieldFilter =
	ReportTypeViewFieldBooleanFilter
	| ReportTypeViewFieldTextualFilter
	| ReportTypeViewFieldNumericFilter
	| ReportTypeViewFieldSignatureFilter
	| ReportTypeViewFieldEquipmentFilter
	| ReportTypeViewFieldOtherFilter;

export type ReportTypeViewTableAdditionalFieldFilter = {
	id: string | number;
	type: ReportTypeViewTableAdditionalFilterType.FIELD;
	value: Nullable<ReportTypeViewFieldFilter>;
};
export type ReportTypeViewTableAdditionalJobFilter = {
	id: string | number;
	type: ReportTypeViewTableAdditionalFilterType.JOB;
	value: Nullable<number[]>;
};

export type ReportTypeViewTableAdditionalFilter = ReportTypeViewTableAdditionalFieldFilter | ReportTypeViewTableAdditionalJobFilter;

export const toQueryParamsReducer = (_tableFilters: ReportTypeViewTableAdditionalFilter[]) => {
	return _tableFilters.reduce<ReportTypeViewTableAPI.ReportTypeViewTableAdditionalFilter[]>((_acc, _currFilter) => {
		if (_currFilter.type === ReportTypeViewTableAdditionalFilterType.JOB) {
			_acc.push(_currFilter);
		} else if (_currFilter.type === ReportTypeViewTableAdditionalFilterType.FIELD) {
			if (_currFilter.value === null) {
				return _acc;
			}
			switch (_currFilter.value.type) {
				case ReportBlockFieldType.IMMUTABLE_TEXT:
				case ReportBlockFieldType.LINE_BREAK:
				case ReportBlockFieldType.IMMUTABLE_IMAGE: {
					console.error('Unsupported field type');
					return _acc;
				}
				case ReportBlockFieldType.EQUIPMENT: {
					_acc.push({
						type: ReportTypeViewTableAdditionalFilterType.FIELD,
						value: {
							type: ReportBlockFieldType.EQUIPMENT,
							value: _currFilter.value.value.map((_equipment) => _equipment?.id ?? null),
							comparator: _currFilter.value.comparator,
							name: _currFilter.value.name,
						},
					});
					break;
				}
				case ReportBlockFieldType.SIGNATURE: {
					_acc.push({
						type: ReportTypeViewTableAdditionalFilterType.FIELD,
						value: {
							type: ReportBlockFieldType.SIGNATURE,
							value: _currFilter.value.value.map((_signee) => _signee?.id ?? null),
							comparator: _currFilter.value.comparator,
							name: _currFilter.value.name,
						},
					});
					break;
				}
				case ReportBlockFieldType.ID:
				case ReportBlockFieldType.TEXTUAL_ATTRIBUTE:
				case ReportBlockFieldType.INFORMATION:
				case ReportBlockFieldType.IMAGE:
				case ReportBlockFieldType.ADDRESS:
				case ReportBlockFieldType.BOOLEAN:
				case ReportBlockFieldType.DROPDOWN:
				case ReportBlockFieldType.COMPLETION:
				case ReportBlockFieldType.TIME: {
					_acc.push({
						value: _currFilter.value,
						type: _currFilter.type,
					});
					break;
				}
				case ReportBlockFieldType.NUMERIC_ATTRIBUTE:
				case ReportBlockFieldType.CALCULATED: {
					if (_currFilter.value.comparator === ComparatorType.EMPTY) {
						_acc.push({
							type: _currFilter.type,
							value: {
								value: null,
								comparator: _currFilter.value.comparator,
								type: _currFilter.value.type,
								name: _currFilter.value.name,
							},
						});
					} else if (_currFilter.value.comparator === ComparatorType.RANGE) {
						_acc.push({
							type: _currFilter.type,
							value: {
								comparator: _currFilter.value.comparator,
								min: _currFilter.value.value.min,
								max: _currFilter.value.value.max,
								name: _currFilter.value.name,
								type: _currFilter.value.type,
							},
						});
					} else {
						_acc.push({
							value: _currFilter.value,
							type: _currFilter.type,
						});
					}
					break;
				}
			}
		}
		return _acc;
	}, []);
};

const LABELS_MAP: Record<ReportTypeViewTableAdditionalFilterType, string> = {
	FIELD: 'Field',
	JOB: 'Job',
};

export const ADDITIONAL_OPTIONS = Object.keys(
	ReportTypeViewTableAdditionalFilterType
).map<{ id: string; label: string; }>((_filter) => ({
	id: _filter,
	label: LABELS_MAP[_filter],
}));
